import React, { useState, useEffect } from "react";
import { Card, Button, Badge, OverlayTrigger, Tooltip } from "react-bootstrap";

import { EntityType, entityTypes } from "../utils/types";
import {
  deleteCustomEntity,
  getCustomEntities,
} from "../services/customentity";
import { ENTITY_COLORS, ENTITY_BORDER_COLORS } from "../utils/constant";
import CustomEntityModal from "./CustomEntityModel";

interface EntityManagementProps {
  onSelectEntity: (entity: string) => void;
  selectedEntity: string;
  // Add a callback for when entities change
  onEntitiesUpdated?: (allEntities: string[], customEntities: any[]) => void;
}

const EntityManagement: React.FC<EntityManagementProps> = ({
  onSelectEntity,
  selectedEntity,
  onEntitiesUpdated,
}) => {
  const [customEntities, setCustomEntities] = useState<any[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Combine built-in and custom entities
  const allEntities = [
    ...entityTypes,
    ...customEntities.map((e) => e.entity_tag),
  ];

  const loadCustomEntities = async () => {
    setIsLoading(true);
    try {
      const entities = await getCustomEntities();
      setCustomEntities(entities);

      // Update colors and border colors in constants
      entities.forEach((entity) => {
        ENTITY_COLORS[entity.entity_tag as EntityType] = entity.metadata.color;
        ENTITY_BORDER_COLORS[entity.entity_tag as EntityType] =
          entity.metadata.borderColor;
      });

      // Notify parent component about updated entities
      if (onEntitiesUpdated) {
        onEntitiesUpdated(
          [...entityTypes, ...entities.map((e) => e.entity_tag)],
          entities
        );
      }
    } catch (error) {
      console.error("Failed to load custom entities:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCustomEntities();
  }, []);

  const handleCreateEntity = () => {
    setShowModal(true);
  };

  const handleEntityCreated = () => {
    loadCustomEntities();
  };

  const handleDeleteEntity = async (id: string, name: string) => {
    if (
      window.confirm(`Are you sure you want to delete the entity "${name}"?`)
    ) {
      const success = await deleteCustomEntity(id);
      if (success) {
        loadCustomEntities();
      }
    }
  };

  return (
    <Card className="shadow-sm border-0 mb-4">
      <Card.Header className="bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Custom Entity</h5>
        <Button
          variant="outline-primary"
          size="sm"
          onClick={handleCreateEntity}
          disabled={isLoading}
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="me-1"
          >
            <line x1="12" y1="5" x2="12" y2="19"></line>
            <line x1="5" y1="12" x2="19" y2="12"></line>
          </svg>
          Create
        </Button>
      </Card.Header>

      <Card.Body>
        {customEntities.length > 0 && (
          <>
            <h6 className="mb-3 mt-4">Custom Entities</h6>
            <div className="d-flex flex-wrap gap-2">
              {customEntities.map((entity) => (
                <OverlayTrigger
                  key={entity.id}
                  placement="top"
                  overlay={<Tooltip>{entity.entity_tag}</Tooltip>}
                >
                  <div
                    className={`border rounded p-2 text-center position-relative ${
                      selectedEntity === entity.entity_tag
                        ? "border-dark border-2"
                        : ""
                    }`}
                    style={{
                      backgroundColor:
                        ENTITY_COLORS[entity.entity_tag as EntityType],
                      borderColor:
                        ENTITY_BORDER_COLORS[entity.entity_tag as EntityType],
                      width: "70px",
                      cursor: "pointer",
                      fontSize: "11px",
                      fontWeight: "bold",
                    }}
                    onClick={() => onSelectEntity(entity.entity_tag)}
                    // onDoubleClick={() =>
                    //   handleDeleteEntity(entity.id, entity.name)
                    // }
                  >
                    <div className="text-truncate" title={entity.entity_tag}>
                      {entity.entity_tag}
                    </div>
                  </div>
                </OverlayTrigger>
              ))}
            </div>
          </>
        )}

        <CustomEntityModal
          show={showModal}
          onHide={() => setShowModal(false)}
          onEntityCreated={handleEntityCreated}
        />
      </Card.Body>
    </Card>
  );
};

export default EntityManagement;
